<template>
  <AdminLayout>
    <Head :title="reportTitle" />

    <div class="headline flex flex-col md:flex-row justify-between items-start md:items-center mx-4 sm:mx-12">
      <h2 class="text-2xl font-semibold text-blue-600">
        {{ reportTitle }}
      </h2>

      <div class="relative inline-flex self-start my-5">
        <div
          v-if="hasFilter('category_id')"
          class="input-group mr-2"
        >
          <select
            v-model="form.category_id"
            id="category"
            class="bg-gray-100"
          >
            <option :value="null">All Categories</option>
            <option
              v-for="category in categories"
              :key="category.id"
              :value="category.id"
            >
              {{ category.name }}
            </option>
          </select>
        </div>

        <div
          v-if="hasFilter('brand_id')"
          class="input-group mr-2"
        >
          <select
            v-model="form.brand_id"
            id="brand"
            class="bg-gray-100"
          >
            <option :value="null">All Brands</option>
            <option
              v-for="brand in brands"
              :key="brand.id"
              :value="brand.id"
            >
              {{ brand.name }}
            </option>
          </select>
        </div>

        <div
          v-if="hasFilter('supplier_id')"
          class="input-group mr-2"
        >
          <select
            v-model="form.supplier_id"
            id="supplier"
            class="bg-gray-100"
          >
            <option :value="null">All Suppliers</option>
            <option
              v-for="supplier in suppliers"
              :key="supplier.id"
              :value="supplier.id"
            >
              {{ supplier.name }}
            </option>
          </select>
        </div>

        <div
          v-if="hasFilter('location_id')"
          class="input-group mr-2"
        >
          <select
            v-model="form.location_id"
            id="product-regulated"
            class="bg-gray-100"
          >
            <option :value="null">All Locations</option>
            <option
              v-for="location in locations"
              :key="location.id"
              :value="location.id"
            >
              {{ location.name }}
            </option>
          </select>
        </div>

        <div
          v-if="hasFilter('space_id')"
          class="input-group mr-2"
        >
          <select
            v-model="form.space_id"
            id="space"
            class="bg-gray-100"
            :disabled="form.location_id === null || form.location_id < 0"
          >
            <option :value="null">All Spaces</option>
            <option
              v-for="space in spaces"
              :key="space.id"
              :value="space.id"
            >
              {{ space.name }}
            </option>
          </select>
        </div>

        <div
          v-if="hasFilter('delivery_method')"
          class="input-group mr-2 bg-gray-100"
        >
          <MultiSelectInput
            v-model="form.delivery_method"
            :options="deliveryMethodOptions"
            placeholder="Delivery Method(s)"
          >
          </MultiSelectInput>
        </div>

        <div
          v-if="hasFilter('start') && hasFilter('end')"
          class="input-group mr-2"
        >
          <select
            v-model="form.range"
            class="bg-gray-100"
          >
            <option
              v-for="(value, key) in ranges"
              :key="key"
              :value="key"
            >
              {{ value }}
            </option>
          </select>
        </div>

        <div
          v-if="hasFilter('start') && hasFilter('end') && form.range === 'custom'"
          class="mr-2"
        >
          <div class="input-group">
            <date-range-picker
              v-model:start="form.start"
              v-model:end="form.end"
            />
          </div>
        </div>

        <button
          @click="download()"
          type="button"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
        >
          <!--<ChevronDownIcon class="-ml-0.5 mr-2 h-5 w-5" aria-hidden="true" />-->
          Download
        </button>
      </div>
    </div>

    <div
      class="text-sm pt-2 mx-4 sm:mx-12 pb-8"
      v-if="report.limit && !loading"
    >
      Showing up to {{ report.limit }} results
    </div>

    <!-- Page is loading with results -->
    <div
      class="mx-4 sm:mx-12"
      v-if="loading"
    >
      Loading...
    </div>
    <!-- END: Page is loading with results -->

    <!-- Page and results have fully loaded -->
    <div
      class="flex-1 bg-gray-100"
      v-else
    >
      <data-table
        :items="tableRows"
        :headers="tableHeadings"
        :actions="[]"
        :selectable="false"
      >
        <template v-slot:rows="slotRow">
          <index-row
            v-slot:rows="slotRow"
            v-bind="slotRow"
          />
        </template>
      </data-table>
    </div>
    <!-- END: Page and results have fully loaded -->
  </AdminLayout>
</template>

<script>
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { Head, Link } from '@inertiajs/vue3'
import { ChevronDownIcon, ArrowCircleDownIcon } from '@heroicons/vue/solid'
import interactsWithCurrentLocation from '@/Mixins/interactsWithCurrentLocation'
import DataTable from '@/Components/Table/DataTable'
import IndexRow from '@/Pages/Reports/Components/IndexRow'
import MultiSelectInput from '@/Components/Forms/MultiSelectInput'
import DateRangePicker from '@/Components/Forms/DateRangePicker'
import accessesCompany from '@/Mixins/accessesCompany'

export default {
  mixins: [interactsWithCurrentLocation, accessesCompany],

  components: {
    AdminLayout,
    Head,
    Link,
    ChevronDownIcon,
    ArrowCircleDownIcon,
    DataTable,
    IndexRow,
    MultiSelectInput,
    DateRangePicker
  },

  props: {
    /**
     * Query filter params.
     */
    params: Object,
    /**
     * The name of the report (key name used in URL).
     */
    name: String,
    /**
     * The report object of data.
     */
    report: Object,
    /**
     * The filters used by the passed report
     */
    showFilters: Array,
    /**
     * Available date ranges to select.
     */
    ranges: Array,
    /**
     * An array of delivery method objects for filtering.
     */
    deliveryMethods: Array,
    /**
     * An array of spaces for the current selected location
     */
    spaces: Array,
    categories: Array,
    brands: Array,
    suppliers: Array
  },

  data() {
    return {
      loading: true,
      form: this.$inertia.form({
        location_id: this.params.location_id,
        space_id: this.params.space_id || null,
        start: this.params.start || null,
        end: this.params.end || null,
        range: this.params.range || null,
        delivery_method: this.params.delivery_method || [],
        category_id: this.params.category_id || null,
        brand_id: this.params.brand_id || null,
        supplier_id: this.params.supplier_id || null
      })
    }
  },

  created() {
    this.loading = false
  },

  watch: {
    'form.location_id': function (newVal, oldVal) {
      this.form.space_id = null
      this.update()
    },
    'form.space_id': function (newVal, oldVal) {
      this.update()
    },
    'form.range': function (newVal, oldVal) {
      if (newVal !== 'custom') {
        this.form.start = this.form.end = null
        this.update()
      }
    },
    'form.start': function (newVal, oldVal) {
      if (this.form.start && this.form.end) {
        this.update()
      }
    },
    'form.end': function (newVal, oldVal) {
      if (this.form.start && this.form.end) {
        this.update()
      }
    },
    'form.delivery_method': function (newVal, oldVal) {
      this.update()
    },
    'form.category_id': function (newVal, oldVal) {
      this.update()
    },
    'form.brand_id': function (newVal, oldVal) {
      this.update()
    },
    'form.supplier_id': function (newVal, oldVal) {
      this.update()
    }
  },

  computed: {
    reportTitle() {
      return this.report.title || 'Reporting'
    },
    tableHeadings() {
      return this.report.headings
    },
    tableRows() {
      return this.report.rows
    },
    deliveryMethodOptions() {
      return _.map(this.deliveryMethods, function (value, key) {
        return {
          label: value,
          value: key
        }
      })
    }
  },

  methods: {
    hasFilter(name) {
      return this.showFilters.indexOf(name) !== -1
    },
    update() {
      this.loading = true

      this.form.get(route('reports.show', { report: this.name }))
    },
    download() {
      window.open(route('reports.show', { ...this.params, report: this.name, download: true }, '_blank'))
    }
  }
}
</script>
